import { useState, useMemo } from 'react';
import { format } from 'date-fns';
import { Download, Search, Eye, EyeOff, BarChart3, Users, Building } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';

import { useWelcomeMessageAnalytics, type WelcomeMessageAnalyticsEvent } from '@/hooks/useWelcomeMessageAnalytics';
import type { WelcomeMessageWithAnalytics } from '@/hooks/useAdminWelcomeMessages';

interface WelcomeMessageAnalyticsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  message: WelcomeMessageWithAnalytics | null;
}

type EventFilter = 'all' | 'view' | 'dismiss';

export function WelcomeMessageAnalyticsDialog({
  open,
  onOpenChange,
  message,
}: WelcomeMessageAnalyticsDialogProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [eventFilter, setEventFilter] = useState<EventFilter>('all');

  const { data: events = [], isLoading } = useWelcomeMessageAnalytics(
    open ? message?.id ?? null : null
  );

  // Filter events based on search and event type
  const filteredEvents = useMemo(() => {
    return events.filter((event) => {
      // Filter by event type
      if (eventFilter !== 'all' && event.eventType !== eventFilter) {
        return false;
      }

      // Filter by search term
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        return (
          event.userName.toLowerCase().includes(term) ||
          event.userEmail.toLowerCase().includes(term) ||
          event.businessName?.toLowerCase().includes(term)
        );
      }

      return true;
    });
  }, [events, eventFilter, searchTerm]);

  // Calculate stats
  const stats = useMemo(() => {
    const views = events.filter(e => e.eventType === 'view').length;
    const dismissals = events.filter(e => e.eventType === 'dismiss').length;
    const dismissalRate = views > 0 ? Math.round((dismissals / views) * 100) : 0;
    return { views, dismissals, dismissalRate };
  }, [events]);

  // Export to CSV
  const handleExportCSV = () => {
    if (!message || filteredEvents.length === 0) return;

    const headers = ['User ID', 'Name', 'Email', 'Role', 'Organization/Business', 'Event Type', 'Timestamp'];
    const rows = filteredEvents.map(event => [
      event.userId,
      event.userName,
      event.userEmail,
      event.userRole,
      event.businessName || '',
      event.eventType,
      new Date(event.timestamp).toISOString(),
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `welcome-message-analytics-${message.id.slice(0, 8)}-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const getRoleBadge = (role: string) => {
    switch (role) {
      case 'vendor':
        return <Badge variant="outline" className="gap-1"><Users className="h-3 w-3" />Vendor</Badge>;
      case 'client':
        return <Badge variant="outline" className="gap-1"><Building className="h-3 w-3" />Client</Badge>;
      case 'admin':
        return <Badge variant="secondary">Admin</Badge>;
      default:
        return <Badge variant="outline">{role}</Badge>;
    }
  };

  const getEventBadge = (eventType: string) => {
    switch (eventType) {
      case 'view':
        return <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100 gap-1"><Eye className="h-3 w-3" />Viewed</Badge>;
      case 'dismiss':
        return <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 gap-1"><EyeOff className="h-3 w-3" />Dismissed</Badge>;
      default:
        return <Badge variant="outline">{eventType}</Badge>;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            Analytics: {message?.title}
          </DialogTitle>
          <DialogDescription>
            Detailed view and dismiss events for this welcome message
          </DialogDescription>
        </DialogHeader>

        {/* Summary Stats */}
        <div className="grid gap-4 grid-cols-3">
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Views</p>
                  <p className="text-2xl font-bold">{stats.views}</p>
                </div>
                <Eye className="h-8 w-8 text-muted-foreground/30" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Dismissed</p>
                  <p className="text-2xl font-bold">{stats.dismissals}</p>
                </div>
                <EyeOff className="h-8 w-8 text-muted-foreground/30" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Dismissal Rate</p>
                  <p className="text-2xl font-bold">{stats.dismissalRate}%</p>
                </div>
                <BarChart3 className="h-8 w-8 text-muted-foreground/30" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by name, email, or business..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={eventFilter} onValueChange={(v) => setEventFilter(v as EventFilter)}>
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="Filter events" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Events</SelectItem>
              <SelectItem value="view">Views Only</SelectItem>
              <SelectItem value="dismiss">Dismissals Only</SelectItem>
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            onClick={handleExportCSV}
            disabled={filteredEvents.length === 0}
          >
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
        </div>

        {/* Events Table */}
        <div className="flex-1 min-h-0 max-h-[400px] overflow-y-auto border rounded-md">
          {isLoading ? (
            <div className="space-y-3 p-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : filteredEvents.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground">
                {events.length === 0
                  ? 'No analytics events recorded yet'
                  : 'No events match your filters'}
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Event</TableHead>
                  <TableHead>Timestamp</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredEvents.map((event) => (
                  <TableRow key={event.id}>
                    <TableCell>
                      <div className="space-y-1">
                        <div className="font-medium">{event.userName}</div>
                        <div className="text-sm text-muted-foreground">{event.userEmail}</div>
                        {event.businessName && (
                          <div className="text-xs text-muted-foreground">{event.businessName}</div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{getRoleBadge(event.userRole)}</TableCell>
                    <TableCell>{getEventBadge(event.eventType)}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {format(new Date(event.timestamp), 'MMM d, yyyy h:mm a')}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>

        <div className="text-sm text-muted-foreground text-center pt-2 border-t">
          Showing {filteredEvents.length} of {events.length} events
        </div>
      </DialogContent>
    </Dialog>
  );
}
